import React, { useState, useEffect } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { X } from "lucide-react"
import { getProblemOwners } from "@/lib/chat-service"

interface AddMembersDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  currentMembers: any[]
  onAddMember?: (member: any) => void
  projectId?: string // Add projectId prop
}

export default function AddMembersDialog({ 
  open, 
  onOpenChange, 
  currentMembers, 
  onAddMember,
  projectId 
}: AddMembersDialogProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [pendingMembers, setPendingMembers] = useState<any[]>([])
  const [availableTeamMembers, setAvailableTeamMembers] = useState<any[]>([])
  const [loading, setLoading] = useState(false)

  // Load available team members from project_members table
  useEffect(() => {
    const loadAvailableMembers = async () => {
      if (open) {
        setLoading(true)
        try {
          const users = await getProblemOwners(projectId) // Pass projectId
          // Transform the data to match the expected format
          const transformedUsers = users.map(user => ({
            id: user.id,
            initials: user.display_name?.split(' ').map((n: string) => n[0]).join('') || '??',
            name: user.display_name || 'Unknown User',
            role: user.role || 'project_member', // Updated to project_member
            display_name: user.display_name,
            email: user.email
          }))
          setAvailableTeamMembers(transformedUsers)
        } catch (error) {
          console.error('Error loading available members:', error)
          setAvailableTeamMembers([])
        } finally {
          setLoading(false)
        }
      }
    }

    loadAvailableMembers()
  }, [open, projectId]) // Add projectId to dependency array

  // Filter available members based on search and exclude current members and pending members
  const currentMemberIds = currentMembers.map(member => member.user?.id || member.user_id)
  const pendingMemberIds = pendingMembers.map(member => member.id)
  const filteredAvailableMembers = availableTeamMembers.filter(member => {
    const matchesSearch = member.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         member.role.toLowerCase().includes(searchQuery.toLowerCase())
    const notCurrentMember = !currentMemberIds.includes(member.id)
    const notPendingMember = !pendingMemberIds.includes(member.id)
    return matchesSearch && notCurrentMember && notPendingMember
  }).slice(0, 3) // Limit to top 3 results

  const handleAddToPending = (member: any) => {
    setPendingMembers(prev => [...prev, member])
  }

  const handleRemoveFromPending = (memberId: string) => {
    setPendingMembers(prev => prev.filter(member => member.id !== memberId))
  }

  const handleDone = async () => {
    // Add all pending members when Done is clicked
    if (onAddMember && pendingMembers.length > 0) {
      for (const member of pendingMembers) {
        await onAddMember(member)
      }
    }
    // Clear pending members and close dialog
    setPendingMembers([])
    onOpenChange(false)
  }

  const handleCancel = () => {
    // Clear pending members and close dialog
    setPendingMembers([])
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader className="flex flex-row items-center justify-between">
          <DialogTitle>Add Members to Channel</DialogTitle>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={() => onOpenChange(false)}
          >
            <X className="w-4 h-4" />
          </Button>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Description */}
          <p className="text-muted-foreground">
            Add new team members to this quality channel by searching for them below or entering their information manually.
          </p>

          {/* Current Members */}
          <div className="space-y-3">
            <h3 className="font-semibold">Current Members</h3>
            <div className="space-y-3">
              {currentMembers.map((member, index) => {
                const user = member.user || member
                const initials = user?.display_name?.split(' ').map((n: string) => n[0]).join('') || '??'
                
                return (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center text-sm font-medium">
                      {initials}
                    </div>
                    <div>
                      <div className="font-medium">{user?.display_name || 'Unknown User'}</div>
                      <div className="text-sm text-muted-foreground">{user?.role || 'Member'}</div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Add New Member */}
          <div className="space-y-3">
            <h3 className="font-semibold">Add New Member</h3>
            <Input
              placeholder="Search by name or role..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full"
            />
          </div>

          {/* Pending Members to Add */}
          {pendingMembers.length > 0 && (
            <div className="space-y-3">
              <h3 className="font-semibold">Members to Add ({pendingMembers.length})</h3>
              <div className="space-y-3">
                {pendingMembers.map((member) => (
                  <div key={member.id} className="flex items-center justify-between bg-green-50 p-3 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center text-sm font-medium">
                        {member.initials}
                      </div>
                      <div>
                        <div className="font-medium">{member.name}</div>
                        <div className="text-sm text-muted-foreground">{member.role}</div>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRemoveFromPending(member.id)}
                    >
                      Remove
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Available Team Members */}
          <div className="space-y-3">
            <h3 className="font-semibold">Available Team Members</h3>
            <div className="space-y-3">
              {loading ? (
                <p className="text-muted-foreground text-sm">Loading team members...</p>
              ) : filteredAvailableMembers.length === 0 ? (
                <p className="text-muted-foreground text-sm">
                  {searchQuery ? 'No members found matching your search.' : 'All available members are already in this channel.'}
                </p>
              ) : (
                filteredAvailableMembers.map((member) => (
                  <div key={member.id} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center text-sm font-medium">
                        {member.initials}
                      </div>
                      <div>
                        <div className="font-medium">{member.name}</div>
                        <div className="text-sm text-muted-foreground">{member.role}</div>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleAddToPending(member)}
                    >
                      Add
                    </Button>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <Button 
              variant="outline" 
              className="flex-1"
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button 
              className="flex-1 bg-slate-900 hover:bg-slate-800 text-white"
              onClick={handleDone}
              disabled={pendingMembers.length === 0}
            >
              Done {pendingMembers.length > 0 && `(${pendingMembers.length})`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
} 